import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, Building2, Loader2 } from "lucide-react";
import { trackFormSubmit } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IMAGES = {
  partner: "https://images.unsplash.com/photo-1752159684779-0639174cdfac?crop=entropy&cs=srgb&fm=jpg&q=85",
};

const formSchema = z.object({
  company_name: z.string().min(2, "Company name is required"),
  contact_person: z.string().min(2, "Contact person is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().optional(),
  country: z.string().min(2, "Country is required"),
  inquiry_type: z.string().min(1, "Please select inquiry type"),
  product_interest: z.array(z.string()).optional(),
  estimated_volume: z.string().optional(),
  message: z.string().min(10, "Please provide more details about your requirements"),
});

const inquiryTypes = [
  { value: "distributor", label: "Distributor" },
  { value: "importer", label: "Importer" },
  { value: "horeca", label: "HoReCa / Hospitality" },
  { value: "institutional", label: "Institutional Buyer" },
  { value: "retailer", label: "Retailer" },
  { value: "other", label: "Other" },
];

const productOptions = [
  { id: "bagasse", label: "Bagasse Tableware" },
  { id: "areca", label: "Areca Leaf Products" },
  { id: "biobags", label: "Biodegradable Bags" },
  { id: "biocutlery", label: "Bio Cutlery" },
  { id: "coconutscrubber", label: "Coconut Scrubbers" },
];

const volumeOptions = [
  { value: "small", label: "< 10,000 units/month" },
  { value: "medium", label: "10,000 - 50,000 units/month" },
  { value: "large", label: "50,000 - 200,000 units/month" },
  { value: "enterprise", label: "> 200,000 units/month" },
];

const PartnerPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      company_name: "",
      contact_person: "",
      email: "",
      phone: "",
      country: "",
      inquiry_type: "",
      product_interest: [],
      estimated_volume: "",
      message: "",
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, data);
      trackFormSubmit("partner_inquiry", "/partner");
      setSubmitted(true);
      toast.success("Inquiry submitted successfully! We'll be in touch soon.");
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="partner-page" className="noise-overlay">
        <section className="pt-32 pb-20 min-h-screen flex items-center">
          <div className="container-custom">
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-8">
                <Building2 className="h-10 w-10 text-primary" strokeWidth={1.5} />
              </div>
              <h1 className="heading-lg text-stone-900 mb-6">
                Thank You for Your <span className="italic">Interest</span>
              </h1>
              <p className="body-base text-stone-600 mb-8">
                We've received your partnership inquiry. Our team will review
                your requirements and get back to you within 2 business days.
              </p>
              <Button
                data-testid="submit-another-btn"
                onClick={() => {
                  setSubmitted(false);
                  form.reset();
                }}
                variant="outline"
                className="rounded-full px-8 py-6 text-base font-medium border-2 border-primary text-primary hover:bg-primary/5"
              >
                Submit Another Inquiry
              </Button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="partner-page" className="noise-overlay">
      <Helmet>
        <title>Partner With Us | Wholesale Biodegradable Packaging Inquiry</title>
        <meta name="description" content="Become a Prithira Global partner. Submit a bulk inquiry for bagasse tableware, areca leaf plates, biodegradable bags, and eco-friendly packaging." />
        <meta name="keywords" content="bulk eco packaging inquiry, biodegradable wholesale partner, sustainable packaging distributor India, eco friendly B2B supplier" />
        <link rel="canonical" href="https://www.prithiraglobal.com/partner" />
        <meta property="og:title" content="Partner With Us | Prithira Global" />
        <meta property="og:description" content="Submit a bulk inquiry for biodegradable packaging — bagasse, areca leaf, bio bags, and more." />
        <meta property="og:url" content="https://www.prithiraglobal.com/partner" />
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-20 bg-sand-200/30">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <span className="caption text-primary mb-4 block">Partnership</span>
            <h1 className="heading-xl text-stone-900 mb-8">
              Partner <span className="italic">With Us</span>
            </h1>
            <p className="body-lg text-stone-600 max-w-3xl mx-auto">
              Join the sustainable packaging revolution. Whether you're a
              distributor, importer, or institutional buyer, we'd love to
              explore partnership opportunities.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <h2 className="heading-md text-stone-900 mb-8">
                Tell Us About Your <span className="italic">Business</span>
              </h2>

              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                  data-testid="partner-form"
                >
                  <div className="grid md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="company_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Company Name *</FormLabel>
                          <FormControl>
                            <Input
                              data-testid="company-name-input"
                              placeholder="Your company name"
                              className="h-12 rounded-lg"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="contact_person"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contact Person *</FormLabel>
                          <FormControl>
                            <Input
                              data-testid="contact-person-input"
                              placeholder="Your name"
                              className="h-12 rounded-lg"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Business Email *</FormLabel>
                          <FormControl>
                            <Input
                              data-testid="email-input"
                              type="email"
                              placeholder="email@company.com"
                              className="h-12 rounded-lg"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone Number</FormLabel>
                          <FormControl>
                            <Input
                              data-testid="phone-input"
                              placeholder="+1 234 567 8900"
                              className="h-12 rounded-lg"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="country"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Country *</FormLabel>
                          <FormControl>
                            <Input
                              data-testid="country-input"
                              placeholder="Your country"
                              className="h-12 rounded-lg"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="inquiry_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Inquiry Type *</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                data-testid="inquiry-type-select"
                                className="h-12 rounded-lg"
                              >
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {inquiryTypes.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="product_interest"
                    render={() => (
                      <FormItem>
                        <FormLabel>Products of Interest</FormLabel>
                        <div className="grid grid-cols-3 gap-4 mt-2">
                          {productOptions.map((product) => (
                            <FormField
                              key={product.id}
                              control={form.control}
                              name="product_interest"
                              render={({ field }) => (
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                  <FormControl>
                                    <Checkbox
                                      data-testid={`product-${product.id}`}
                                      checked={field.value?.includes(product.id)}
                                      onCheckedChange={(checked) => {
                                        return checked
                                          ? field.onChange([
                                              ...field.value,
                                              product.id,
                                            ])
                                          : field.onChange(
                                              field.value?.filter(
                                                (value) => value !== product.id
                                              )
                                            );
                                      }}
                                    />
                                  </FormControl>
                                  <FormLabel className="text-sm font-normal cursor-pointer">
                                    {product.label}
                                  </FormLabel>
                                </FormItem>
                              )}
                            />
                          ))}
                        </div>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="estimated_volume"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Estimated Monthly Volume</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger
                              data-testid="volume-select"
                              className="h-12 rounded-lg"
                            >
                              <SelectValue placeholder="Select volume" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {volumeOptions.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Requirements & Message *</FormLabel>
                        <FormControl>
                          <Textarea
                            data-testid="message-textarea"
                            placeholder="Tell us about your business needs, specific product requirements, or any questions you have..."
                            className="min-h-[150px] rounded-lg resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    data-testid="submit-inquiry-btn"
                    disabled={isSubmitting}
                    className="w-full rounded-full py-6 text-base font-medium bg-primary text-primary-foreground hover:bg-primary/90 btn-lift"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Inquiry
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                </form>
              </Form>
            </div>

            {/* Info */}
            <div>
              <div className="rounded-2xl overflow-hidden mb-8">
                <img
                  src={IMAGES.partner}
                  alt="Partnership"
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
              <div className="space-y-8">
                <div>
                  <h3 className="font-heading text-xl text-stone-900 mb-3">
                    Who We Partner With
                  </h3>
                  <ul className="space-y-2 text-stone-600 font-light text-sm">
                    <li>• Distributors seeking sustainable product lines</li>
                    <li>• International importers looking for Indian-made eco products</li>
                    <li>• HoReCa suppliers serving hospitality industries</li>
                    <li>• Institutional buyers for cafeterias and canteens</li>
                    <li>• Retailers committed to eco-friendly offerings</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-heading text-xl text-stone-900 mb-3">
                    What to Expect
                  </h3>
                  <ul className="space-y-2 text-stone-600 font-light text-sm">
                    <li>• Response within 2 business days</li>
                    <li>• Detailed product specifications and pricing</li>
                    <li>• Sample arrangements for qualified partners</li>
                    <li>• Customization options discussion</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnerPage;
